// angular
import {
  Component,
  forwardRef,
  inject,
  input,
  OnInit,
  output,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
// components
import { SelectDropdownComponent } from '@core/components/select-dropdown/select-dropdown.component';
import { InputComponent } from '@core/components/input/input.component';
// services
import { LoadingService } from '@app/shared/services/loading.service';
// interfaces & constants
import { IDropdown } from '@app/shared/types/dropdown.interface';
import { CountryCodes } from '@shared/constants/country-codes.const';
// directives
import { OnlyNumberDirective } from '@app/shared/directives/only-number.directive';
import { IPhoneNumber } from '@app/core/shared/interfaces/phone-number.interface';

@Component({
  selector: 'pwj-phone-number',
  standalone: true,
  imports: [
    SelectDropdownComponent,
    InputComponent,
    ReactiveFormsModule,
    OnlyNumberDirective,
    TranslateModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneNumberComponent),
      multi: true,
    },
  ],
  templateUrl: './phone-number.component.html',
  styleUrl: './phone-number.component.scss',
})
export class PhoneNumberComponent implements OnInit, ControlValueAccessor {
  label = input<string>('');
  subLabel = input<string>('');
  placeholder = input<string>('');
  isDisabled = input<boolean>(false);
  icon = input<string>('');
  messages = input<{ [errorCode: string]: string }>({});

  focus = output<boolean>();
  blur = output<boolean>();

  public value: string | null = null;
  public disabled = false;
  public countryCodes: IDropdown[];
  public phoneNumberForm: FormGroup;

  // dependencies
  private readonly _fb = inject(FormBuilder);
  public readonly loadingService = inject(LoadingService);

  constructor() {
    // set country codes
    this.countryCodes = CountryCodes.map(code => ({
      value: code.dial_code,
      label: `${code.code}+${code.name}${code.dial_code}`,
    }));

    // initialize phone number form
    this.phoneNumberForm = this._fb.group({
      prefix: ['+98'],
      mobile_number: ['', [Validators.required, this.validateMobileNumber()]],
    });
  }

  validateMobileNumber(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value || !/^\d+$/.test(value)) {
        return { invalidMobile: true };
      }

      const length = value.length;

   
      // if (length > 8 && length < 12 && value.startsWith('0')) {
      //   return null; // Valid 9-digit number starting with 0
      // } else

      if (length > 7 && length < 12) {
        return null; // Valid 7-digit number without starting with 0
      } else {
        return { invalidMobile: true };
      }
    };
  }

  ngOnInit() {
    // disable mode
    this.isDisabled && this.setDisabledState(this.isDisabled());

    // track value changes in phone number form
    this.phoneNumberForm.valueChanges.subscribe((val: IPhoneNumber) => {
      if (this.phoneNumberForm.valid) {
        this.propagateChange(val);
      } else {
        this.propagateChange(null);
      }
    });

    // track form status changes
    this.phoneNumberForm.statusChanges.subscribe(() => {
      this.onTouched();
    });
  }

  get f() {
    return this.phoneNumberForm.controls;
  }

  propagateChange = (_: IPhoneNumber | null) => {};

  onTouched: () => void = () => {};

  writeValue(value: string) {
    this.phoneNumberForm.get('mobile_number')?.setValue(value);
  }

  registerOnChange(fn: () => void): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  validate(): ValidationErrors | null {
    return this.phoneNumberForm.valid ? null : { invalidPhoneNumber: true };
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.f['prefix'].disable();
      this.f['mobile_number'].disable();
    }
    this.disabled = isDisabled;
  }
}
